import { min } from 'd3-array';
import { filterObj, mapObjToArr } from '../../object-helpers';

export function positionIndividualTooltips(layout, tooltips, series, closestPoint) {

	// Minimum spacing between tooltips (pixels)
	const minTooltipSpacing = 3;

	// Tooltips should not protrude above this top limit
	const topLimit = layout.plotAreaY + 5;

	// Tooltips should not protrude below this bottom limit
	const bottomLimit = layout.plotAreaY + layout.plotAreaHeight - 5;

	// Collect positional/dimensional information for each tooltip, but only for enabled tooltips.
	const yTooltips = filterObj(tooltips.y, (name, tt) => tt.isEnabled);

	const anyYTooltips = Object.keys(yTooltips).length > 0;

	const tooltipDimensions = anyYTooltips
		? getTooltipDimensions(yTooltips, series, closestPoint)
		: [];

	// Dimensions of the x tooltips
	const xTooltipWidth = tooltips.x === null ? -1 : tooltips.x.top.node().offsetWidth;
	const xTooltipHeight = tooltips.x === null ? -1 : tooltips.x.top.node().offsetHeight;

	// Find the width of the widest tooltip
	// const maxYTooltipWidth = max(tooltipDimensions.map(dim => dim.width));
	// const maxTooltipWidth = Math.max(xTooltipWidth, maxYTooltipWidth);

	// If the widest tooltip does not comfortably fit on the left side of the closest point,
	// we will display all tooltips on the right side of the closest data point instead
	const displayOnRightSide = closestPoint.x < 160; // prevent flickering of which side tooltip sits on
	// const displayOnRightSide = closestPoint.x - maxTooltipWidth - 10 < layout.plotAreaX + 10;

	if (tooltipDimensions.length > 0) {

		// Calculate the left position for each tooltip.
		tooltipDimensions.forEach(dim => {
			dim.left = displayOnRightSide
				? closestPoint.x + 10
				: closestPoint.x - dim.width - 10;
		});

		// Sort from top to bottom
		tooltipDimensions.sort((a, b) => a.top - b.top);

		let overlaps = computeOverlaps(tooltipDimensions, topLimit, bottomLimit);

		// Iterate through a positioning algorithm which attempts to space the tooltips
		// out and remove any overlaps. Set a max iteration limit in case this isn't possible.
		let i = 0;

		while (min(overlaps) < minTooltipSpacing && i < 10) {

			adjustPositions(tooltipDimensions, overlaps, minTooltipSpacing);

			// Get updated set of overlaps after adjustments have been performed.
			overlaps = computeOverlaps(tooltipDimensions, topLimit, bottomLimit);

			i++;

		}

		// Now set the positions of the tooltip elements!
		tooltipDimensions.forEach(dim => {
			tooltips.y[dim.name].elem
				.style('left', `${dim.left}px`)
				.style('top', `${dim.top}px`);
		});

	}

	if (tooltips.x !== null) {

		// Find the left position of the X tooltips
		const xTooltipLeft = displayOnRightSide
			? closestPoint.x + 10
			: closestPoint.x - xTooltipWidth - 10;

		// Position the top X tooltip
		tooltips.x.top
			.style('left', `${xTooltipLeft}px`)
			.style('top', `${layout.plotAreaY - xTooltipHeight - 5}px`);

		// Position the bottom Y tooltip
		tooltips.x.bottom
			.style('left', `${xTooltipLeft}px`)
			.style('top', `${layout.plotAreaY + layout.plotAreaHeight + 5}px`);

	}

}

function getTooltipDimensions(tooltips, series, closestPoint) {
	return mapObjToArr(tooltips, (name, tooltip) => {

		const currentSeries = series[name];
		const currentSeriesValue = currentSeries.config.dataAccessor(closestPoint.value);
		const y = currentSeries.associatedScale(currentSeries.config.type === 'area' ? currentSeriesValue[1] : currentSeriesValue);

		const node = tooltip.elem.node();

		const w = node.offsetWidth;
		const h = node.offsetHeight;
		const top = y - h - 10;

		return {
			name,
			top,
			bottom: top + h,
			width: w,
			height: h
		};
	});
}

function adjustPositions(tooltipDimensions, overlaps, minTooltipSpacing) {

	const tooltipCount = tooltipDimensions.length;

	if (overlaps[0] < 0) {

		// If the top tooltip overlaps the top limit, adjust it downwards.
		const shiftDownBy = -overlaps[0];
		tooltipDimensions[0].top += shiftDownBy;
		tooltipDimensions[0].bottom += shiftDownBy;

	} else if (overlaps[overlaps.length - 1] < 0) {

		// If the bottom tooltip overlaps the bottom limit, adjust it upwards.
		const shiftUpBy = -overlaps[overlaps.length - 1];
		tooltipDimensions[tooltipCount - 1].top -= shiftUpBy;
		tooltipDimensions[tooltipCount - 1].bottom -= shiftUpBy;

	} else {

		// Otherwise iterate through the middle tooltips and adjust them to try and remove
		// any overlaps.

		for (let j = 1, n = overlaps.length; j < n - 1; j++) {

			if (overlaps[j] < minTooltipSpacing) {

				// The total amount that this tooltip-pair needs shifting apart. This will be split,
				// with the top tooltip being pushed upwards and the bottom tooltip being pushed
				// downwards, if possible.
				const shiftAmount = Math.abs(overlaps[j] - minTooltipSpacing);

				let shiftAmountTop;
				let shiftAmountBottom;

				if (j === 1) {

					// Overlap between top-most tooltip and the one directly below it.
					// Only shift up the top-most item up if there is room to do so.
					const canShiftUp = overlaps[0] > 0;

					if (canShiftUp) {

						shiftAmountTop = Math.min(0.5 * shiftAmount, overlaps[0]);
						shiftAmountBottom = shiftAmount - shiftAmountTop;

					} else {

						shiftAmountTop = 0;
						shiftAmountBottom = shiftAmount;

					}

				} else if (j === n - 2) {

					// Overlap between the bottom-most tooltip and the one directly above it.
					// Only shift down the bottom-most item if there is room to do so.
					const canShiftDown = overlaps[n - 1] > 0;

					if (canShiftDown) {

						shiftAmountBottom = Math.min(0.5 * shiftAmount, overlaps[n - 1]);
						shiftAmountTop = shiftAmount - shiftAmountBottom;

					} else {

						shiftAmountBottom = 0;
						shiftAmountTop = shiftAmount;

					}

				} else {

					// For other overlaps, shift the top one up and the bottom one down by equal amounts.
					shiftAmountTop = 0.5 * shiftAmount;
					shiftAmountBottom = 0.5 * shiftAmount;

				}

				// Adjust position of upper tooltip in the pair
				tooltipDimensions[j - 1].top -= shiftAmountTop;
				tooltipDimensions[j - 1].bottom -= shiftAmountTop;

				// Adjust position of lower tooltip in the pair
				tooltipDimensions[j].top += shiftAmountBottom;
				tooltipDimensions[j].bottom += shiftAmountBottom;

			}

		}

	}
}

function computeOverlaps(tooltipDimensions, topLimit, bottomLimit) {

	const n = tooltipDimensions.length;

	// The first overlap is between the top limit and the top-most tooltip.
	const overlaps = [
		tooltipDimensions[0].top - topLimit
	];

	for (let i = 1; i < n; i++) {
		overlaps.push(tooltipDimensions[i].top - tooltipDimensions[i - 1].bottom);
	}

	// The last overlap is between the bottom-most tooltip and the bottom limit.
	overlaps.push(
		bottomLimit - tooltipDimensions[n - 1].bottom
	);

	return overlaps;

}



// WEBPACK FOOTER //
// ./utils/panelgraph/functions/position-individual-tooltips.js